/**
 * MUI (Tabs, useMediaQuery, TextareaAutosize, etc.) can trigger
 * "ResizeObserver loop completed with undelivered notifications" when
 * observer callbacks mutate layout in the same frame. Debouncing via rAF
 * avoids the loop; the error listener covers any remaining browser noise.
 *
 * rAF-deferred callbacks can run after the observed node unmounts (e.g. closing
 * a dialog). TextareaAutosize then calls getComputedStyle on a null ref — skip
 * those stale notifications and swallow the resulting TypeError.
 */
const RESIZE_OBSERVER_LOOP_ERROR =
  'ResizeObserver loop completed with undelivered notifications'

const GET_COMPUTED_STYLE_ELEMENT_ERROR =
  "Failed to execute 'getComputedStyle' on 'Window': parameter 1 is not of type 'Element'."

function isStaleMuiResizeError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    error.message.includes('getComputedStyle') &&
    error.message.includes('not of type')
  )
}

if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
  const NativeResizeObserver = window.ResizeObserver
  window.ResizeObserver = class extends NativeResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        requestAnimationFrame(() => {
          const liveEntries = entries.filter(
            (entry) =>
              entry.target instanceof Element && entry.target.isConnected
          )
          if (liveEntries.length === 0) {
            return
          }
          try {
            callback(liveEntries, observer)
          } catch (error) {
            if (!isStaleMuiResizeError(error)) {
              throw error
            }
          }
        })
      })
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (
      event.message === RESIZE_OBSERVER_LOOP_ERROR ||
      event.message === GET_COMPUTED_STYLE_ELEMENT_ERROR
    ) {
      event.stopImmediatePropagation()
    }
  })
}

export {}
